import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";

import {
  getPlantDetails,
  getColumnMapping,
  getProceedToAnalysis,
  setState,
  getDefaultCIPValues,
} from "../../network/cipAPI";
import {
  storeFileId,
  storeApp,
  storeDefaultInformation,
} from "../../Store/TemplateStore/template.action";

import Header from "../../components/Header";
import DupontButton from "../../components/DupontButton";
import PerformanceData from "../performanceData";
import PageLoading from "../../components/PageLoading";
import { OverlayLoader } from "../../components/PageLoading";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../components/Notifications";

import { ValueStrings } from "../../utils/constantsString";
import { extractPlantId, getSelectedPlantTrainInfo } from "../../utils/Utils";
import { getItemSession } from "../../utils/session";

import ColumnSelection from "./columnSelection";
import ReferenceCondition from "./referenceCondition";
import DefaultCIPInformation from "../defaultCIPInformation";
import { DEFAULT_CIP_KEYS } from "../../constants/global.constants";
import AdvanceSettings from "./advancedSettings";
import NormalizationCheck from "./normalizationCheck";
import { API_URLS } from "../../network/appUrls";
import FileUpload from "../fileUpload";
import { updateSelectedTrain } from "../../Store/TemplateStore/template.action";
import CIPSpecification from "../../components/CIPSpecification";

import "./index.scss";

//const isLocalhost = window.location.hostname === "localhost";
//const basePath = isLocalhost ? "/" : "/roadvisor/";
const basePath = API_URLS.basePath;

const AnalyseData = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const name = getItemSession("name");
  const waterType = getItemSession("water_type");
  const plantPageURL = getItemSession("plantPageURL");

  const data = location.state;
  const token = useSelector((state) => state.token);
  const searchParams = new URLSearchParams(window.location.search);
  const isEdit = searchParams.get("isEdit");
  const showRecurrent = data?.showRecurrent || false;

  const userName = useSelector((state) => state.userName);
  const [columnsOption, setColumnsOptions] = useState("");
  const [fileId, setFileId] = useState("");
  const [mappedColumn, setMappedColumn] = useState([]);
  const [config, setConfig] = useState([]);
  const [mappedReference, setMappedReference] = useState([]);
  const [datasetDetails, setDatasetDetails] = useState("");
  const [fileInfo, setFileInfo] = useState([]);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [isNormalizationColumnFilled, setIsNormalizationColumnFilled] =
    useState(false);
  const [isNormalizationPopUpOpen, setIsNormalizationPopUpOpen] =
    useState(false);
  const [plantName, setPlantName] = useState(name);
  const [water_type, setWater_type] = useState(waterType);
  const [isReferenceEnabled, setisReferenceEnabled] = useState(false);
  const [isProceedEnabled, setIsProceedEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedColumnRadioColumn, setSelectedColumnRadioColumn] = useState({
    ["column5"]: "feed_flowrate",
    ["column8"]: "concentrate_pressure",
  });
  const [selectedTrain, setSelectedTrain] = useState();
  const [disableTrain, setDisableTrain] = useState();
  const [selectedSheet, setSelectedSheet] = useState();
  const plantId = extractPlantId();
  const plantData = useSelector((state) => state.plantData);
  const sel_train = getSelectedPlantTrainInfo(plantData, plantId);
  const accessUserPlant = useSelector((state) => state.accessUserPlant);
  const [selectedCIPFileName, setSelectedCIPFileName] = useState(null);
  const [hasRecurrentCIPs, setHasRecurrentCIPs] = useState(false);
  const handleSettingsDialogOpen = () => {
    setIsSettingsDialogOpen(true);
  };
  const handleSettingsDialogClose = () => {
    setIsSettingsDialogOpen(false);
  };

  const getFormattedDate = (dateString) => {
    const date = moment(dateString).isValid();
    return date ? moment(dateString).format(ValueStrings.dateFormat) : "";
  };

  useEffect(() => {
    if (selectedTrain === undefined) {
      setSelectedTrain(1);
    }
  }, [selectedTrain]);

  useEffect(() => {
    const getPlantDetail = async () => {
      setIsLoading(true);
      try {
        const response = await getPlantDetails(plantId, true, sel_train);
        if (response && response.data) {
          const {
            file,
            parent_file_name,
            col_mapping,
            sheet_names,
            sel_train,
            bulk_cip_file_name,
            has_recurrent_cips,
            config,
            default_cip,
            ref_condition,
            name: plantName,
            water_type,
            analysis,
          } = response.data;

          let file_id, sheet_name, min_date, max_date;
          if (file) {
            ({ file_id, sheet_name, min_date, max_date } = file);
          }

          const fileInfo = {
            file_id,
            sheetName: sheet_name,
            fileName: parent_file_name,
          };
          dispatch(storeFileId(fileInfo));
          setFileInfo(fileInfo);
          setSelectedSheet(sheet_name);
          setFileId(file_id);
          setMappedColumn(col_mapping);
          setSheetNames(sheet_names);
          setSelectedTrain(sel_train);
          setSelectedCIPFileName(bulk_cip_file_name);
          setHasRecurrentCIPs(has_recurrent_cips);
          dispatch(updateSelectedTrain(sel_train));
          setConfig(config);
          setIsProceedEnabled(!analysis);
          setMappedReference(ref_condition?.[0]);
          setDatasetDetails(
            ` ${userName}_ ${plantName}_ ${water_type}
            ${
              getFormattedDate(min_date)
                ? `_ ${getFormattedDate(min_date)}`
                : ""
            }
            ${
              getFormattedDate(max_date)
                ? `to ${getFormattedDate(max_date)}`
                : ""
            }`
          );
          if (default_cip) {
            dispatch(storeDefaultInformation({ ...default_cip }));
          }

          if (file_id) {
            const columns = await getColumnMapping(file_id, sel_train);
            if (columns && columns.data) {
              setColumnsOptions(columns.data?.cols);
            }
          }
        }
      } catch (err) {
        console.log("getPlantDetail..", err);
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.somethingWrong"),
          ""
        );
      } finally {
        setIsLoading(false);
      }
    };
    dispatch(storeApp(null));
    if (token) getPlantDetail();
  }, [token]);
  useEffect(() => {
    if (showRecurrent) {
      setHasRecurrentCIPs(true);
    }
  }, [showRecurrent]);

  useEffect(() => {
    if (!fileId) {
      setDatasetDetails(` ${userName}_ ${name}_ ${water_type}`);
    }
  }, [fileId]);

  const onExitClick = async () => {
    try {
      const response = await setState(2, sel_train);

      if (accessUserPlant) {
        navigate(`${basePath}user-management`);
      } else if (plantPageURL) {
        navigate(plantPageURL);
      } else {
        navigate(`${basePath}plant`);
      }
    } catch (error) {
      console.log("onExitClick", error);
    }
  };

  const onProceedClick = async () => {
    setIsLoading(true);
    try {
      if (isNormalizationColumnFilled) {
        const response = await getProceedToAnalysis(fileId, selectedTrain);
        if (response && response.status === 200) {
          dispatch(updateSelectedTrain(selectedTrain));
          const path = `${basePath}home?plantId=${plantId}`;
          navigate(path);
        } else {
          createNotification(
            ERROR_NOTIFICATION,
            response.data.detail.error,
            ""
          );
        }
      } else {
        setIsNormalizationPopUpOpen(true);
      }
    } catch (error) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        ""
      );
      console.log("onProceedClick", error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-main">
      <Header />

      <div className="main-container descriptionText font-14">
        <>
          {isLoading && <OverlayLoader />}
          {/* <div className="calculatedValueText">{t("analyseData.selectData")}</div> */}
          <div>
            <PerformanceData
              fileId={fileId}
              fileInfo={fileInfo}
              setFileInfo={setFileInfo}
              selectedTrain={selectedTrain}
              setSelectedTrain={setSelectedTrain}
              setSelectedCIPFileName={setSelectedCIPFileName}
              setHasRecurrentCIPs={setHasRecurrentCIPs}
              setDisableTrain={setDisableTrain}
              setSelectedSheet={setSelectedSheet}
              setMappedColumn={setMappedColumn}
              setMappedReference={setMappedReference}
              sheetNames={sheetNames}
              setSheetNames={setSheetNames}
              setColumnsOptions={setColumnsOptions}
              setFileId={setFileId}
            />
            <FileUpload
              fileInfo={fileInfo}
              config={config}
              setSelectedTrain={setSelectedTrain}
              selectedTrain={selectedTrain}
              disableTrain={disableTrain}
              selectedSheet={selectedSheet}
              sheetNames={sheetNames}
              setFileId={setFileId}
              setMappedColumn={setMappedColumn}
              setColumnsOptions={setColumnsOptions}
              setMappedReference={setMappedReference}
              setSelectedSheet={setSelectedSheet}
              setSelectedCIPFileName={setSelectedCIPFileName}
              setHasRecurrentCIPs={setHasRecurrentCIPs}
              setSheetNames={setSheetNames}
              setIsLoading={setIsLoading}
              setIsProceedEnabled={setIsProceedEnabled}
            />
          </div>
          <div className="flex-ai-center container-padding">
            <span className="tealText">{t("analyseData.datasetName")} </span>
            &nbsp;
            {datasetDetails}
          </div>

          <div className="marginTop5 ">
            <ColumnSelection
              columnsOption={columnsOption}
              setColumnsOptions={setColumnsOptions}
              fileId={fileId}
              isEdit={isEdit}
              mappedColumn={mappedColumn}
              mappedReference={mappedReference}
              setDatasetDetails={setDatasetDetails}
              plantName={plantName}
              water_type={water_type}
              selectedRadioColumn={selectedColumnRadioColumn}
              setSelectedRadioColumn={setSelectedColumnRadioColumn}
              setMappedReference={setMappedReference}
              setIsProceedEnabled={setIsProceedEnabled}
              setIsNormalizationColumnFilled={setIsNormalizationColumnFilled}
              config={config}
              setSelectedTrain={setSelectedTrain}
              selectedTrain={selectedTrain}
            />
          </div>
          <div className="margin-top-20 ">
            <ReferenceCondition
              fileId={fileId}
              isEdit={isEdit}
              mappedReference={mappedReference}
              selectedColumnRadioColumn={selectedColumnRadioColumn}
              setisReferenceEnabled={setisReferenceEnabled}
              setIsProceedEnabled={setIsProceedEnabled}
              selectedTrain={selectedTrain}
            />
            <DefaultCIPInformation
              selectedTrain={selectedTrain}
              defaultCIPKeys={DEFAULT_CIP_KEYS}
            />
          </div>

          <div className="margin-top-20">
            <CIPSpecification
              selectedTrain={selectedTrain}
              selectedCIPFileName={selectedCIPFileName}
              hasRecurrentCIPs={hasRecurrentCIPs}
              shouldScrollToRecurrent={showRecurrent}
              mappedReference={mappedReference}
            />
          </div>

          <div className="margin-top-20 flex-between ">
            <DupontButton
              customClass="margin-left-10"
              onClick={handleSettingsDialogOpen}
            >
              {t("analyseData.settings")}
            </DupontButton>
            <div>
              <DupontButton onClick={onExitClick} customClass="margin-right-20">
                {" "}
                {t("ValueStrings.exit")}
              </DupontButton>
              <DupontButton
                disabled={isProceedEnabled}
                onClick={onProceedClick}
              >
                {" "}
                {t("analyseData.proceedtoAnalysis")}
              </DupontButton>
            </div>
          </div>
        </>
      </div>
      {isNormalizationPopUpOpen && (
        <NormalizationCheck
          fileId={fileId}
          isNormalizationPopUpOpen={isNormalizationPopUpOpen}
          setIsNormalizationPopUpOpen={setIsNormalizationPopUpOpen}
          selectedTrain={selectedTrain}
        />
      )}
      {isSettingsDialogOpen && (
        <AdvanceSettings
          isSettingsDialogOpen={isSettingsDialogOpen}
          handleSettingsDialogClose={handleSettingsDialogClose}
          selectedTrain={selectedTrain}
          outlierRemoval
          cipAndShutdownIdentification
        />
      )}
    </div>
  );
};
export default AnalyseData;
